import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, DatePicker, Drawer, Form, Input, Row, Select, Space, Tabs, Tag, Tooltip, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import ReminderService, { ACTIVITY_TYPES, ActivityType, REMINDER_STATE_TYPES, ReminderStateType, ReminderTagOption } from '../../services/ReminderService';
import { ACTIVITY_LABELS, ACTIVITY_COLORS } from '../../views/Reminders/constants';
import { STATE_LABELS } from '../../views/Reminders/stateLabels';
import UserService from '../../services/UserService';
import { hasRole, normalizeTurkish, openNotification } from '../../utils';
import { http } from '../../http-common';
import TaggableMultiSelect from '../TaggableMultiSelect';
import {
  ESTIMATED_DURATION_TOOLTIP_LINES,
  ESTIMATED_DURATION_VALIDATION_MESSAGE,
  formatEstimatedDurationMinutes,
  parseEstimatedDurationInput,
} from './estimatedDurationUtils';
import {
  Agreement02Icon,
  CursorInWindowIcon,
  GoogleDocIcon,
  Mail02Icon,
  MapsLocation01Icon,
  MessageMultiple01Icon,
  SmartPhone01Icon,
  StarIcon,
  TaskDaily01Icon,
} from 'hugeicons-react';

const REMINDER_ALLOWED_ROLES = ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'INSPECTOR'];
const REMINDER_CHANNEL_OPTIONS = [
  { value: 'PHONE', label: 'Telefon', icon: SmartPhone01Icon },
  { value: 'IN_PERSON', label: 'Yüz yüze', icon: MapsLocation01Icon },
  { value: 'ONLINE', label: 'Online', icon: CursorInWindowIcon },
  { value: 'EMAIL', label: 'E-posta', icon: Mail02Icon },
  { value: 'MESSAGE', label: 'Mesaj', icon: MessageMultiple01Icon },
  { value: 'SOFTWARE_PORTAL_FORM', label: 'Yazılım / Portal / Form', icon: TaskDaily01Icon },
  { value: 'DOCUMENT_RECORD', label: 'Doküman / Kayıt', icon: GoogleDocIcon },
  { value: 'OFFICIAL_LETTER', label: 'Resmi Yazı / Tebligat', icon: Agreement02Icon },
  { value: 'OTHER', label: 'Diğer', icon: StarIcon },
];

const normalizeChannelValue = (channel?: string) => {
  if (!channel) return channel;
  const normalized = String(channel).trim().toUpperCase();
  if (normalized === 'OFFLINE' || normalized === 'LOCATION') return 'IN_PERSON';
  if (normalized === 'MEET') return 'ONLINE';
  if (normalized === 'DOCUMENT') return 'DOCUMENT_RECORD';
  if (normalized === 'SYSTEM') return 'SOFTWARE_PORTAL_FORM';
  if (REMINDER_CHANNEL_OPTIONS.some((option) => option.value === normalized)) {
    return normalized;
  }
  return channel;
};

const toNumericId = (value: any): number | undefined => {
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed <= 0) return undefined;
  return parsed;
};

const getUserId = (user: any): number | undefined => toNumericId(user?.id);

const isUserActive = (user: any): boolean => {
  if (!user) return false;
  if (user?.status === false || user?.enabled === false) return false;
  return true;
};

const hasAllowedReminderRole = (user: any): boolean => {
  const roleNames = (user?.roles ?? []).map((r: any) => r?.name).filter(Boolean);
  return roleNames.some((name: string) => REMINDER_ALLOWED_ROLES.includes(name));
};

const sortUsersByName = (userList: any[]) =>
  [...userList].sort((a, b) => {
    const nameA = `${a?.firstName ?? ''} ${a?.lastName ?? ''}`.trim().toLocaleLowerCase('tr-TR');
    const nameB = `${b?.firstName ?? ''} ${b?.lastName ?? ''}`.trim().toLocaleLowerCase('tr-TR');
    return nameA.localeCompare(nameB, 'tr-TR');
  });

const estimatedDurationTooltipContent = (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {ESTIMATED_DURATION_TOOLTIP_LINES.map((line) => (
      <div key={line}>{line}</div>
    ))}
  </div>
);

export type ReminderFormValues = {
  accountId?: number;
  companyId?: number;
  proposalCompanyId?: number;
  proposalId?: number;
  reminderDate?: dayjs.Dayjs;
  dueDate?: dayjs.Dayjs;
  estimatedDurationMinutes?: number;
  estimatedDurationInput?: string;
  activityType: ActivityType;
  channel?: string;
  content?: string;
  link?: string;
  state?: ReminderStateType;
  status?: ReminderStateType | { id?: number; name?: string; color?: string };
  statusId?: number;
  result?: string;
  entityTab: 'COMPANY' | 'PROPOSAL';
  images?: UploadFile[];
  documents?: UploadFile[];
  resultImages?: UploadFile[];
  resultDocuments?: UploadFile[];
  attachments?: UploadFile[];
  resultAttachments?: UploadFile[];
  duplicateContent?: string;
  duplicateReminderDate?: dayjs.Dayjs;
  properties?: Record<string, any>;
  duplicateOnSave?: boolean;
  tags?: string[];
};

interface ReminderDrawerProps {
  open: boolean;
  isEdit: boolean;
  isAdmin: boolean;
  users?: any[];
  companies: any[];
  proposals: any[];
  initialValues?: Partial<ReminderFormValues>;
  onClose: () => void;
  onSubmit: (values: ReminderFormValues) => Promise<void> | void;
  hideEntityTabs?: boolean;
  fixedCompanyId?: number;
  fixedCompanyName?: string;
  fixedProposalId?: number;
  fixedProposalLabel?: string;
  maxReminderDate?: dayjs.Dayjs;
  canComplete?: boolean;
}

const ReminderDrawer: React.FC<ReminderDrawerProps> = ({
  open,
  isEdit,
  isAdmin,
  canComplete = false,
  users,
  companies,
  proposals,
  initialValues,
  onClose,
  onSubmit,
  hideEntityTabs = false,
  fixedCompanyId,
  fixedCompanyName,
  fixedProposalId,
  fixedProposalLabel,
  maxReminderDate,
}) => {
  const [form] = Form.useForm();
  const initialTab = hideEntityTabs ? (initialValues?.entityTab || 'COMPANY') : initialValues?.entityTab || 'COMPANY';
  const [entityTab, setEntityTab] = useState<'COMPANY' | 'PROPOSAL'>(initialTab);
  const [proposalCompanyId, setProposalCompanyId] = useState<number | null>((initialValues?.proposalCompanyId as number) ?? null);
  const [internalUsers, setInternalUsers] = useState<any[]>([]);
  const [selectedUserOverride, setSelectedUserOverride] = useState<any | null>(null);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [attachments, setAttachments] = useState<UploadFile[]>([]);
  const [resultAttachments, setResultAttachments] = useState<UploadFile[]>([]);
  const [tagOptions, setTagOptions] = useState<ReminderTagOption[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);

  const activityTypeValue = Form.useWatch('activityType', form);
  const proposalIdValue = Form.useWatch('proposalId', form);
  const stateValue = Form.useWatch('state', form);
  const selectedAccountIdValue = Form.useWatch('accountId', form);
  const showMeetingFields = activityTypeValue === 'MEETING_OFFLINE' || activityTypeValue === 'MEETING_ONLINE';
  const linkLabel = activityTypeValue === 'MEETING_OFFLINE' ? 'Lokasyon (Harita URL)' : 'Toplantı Linki';
  const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
  const DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/acad',
    'application/autocad',
    'application/x-acad',
    'application/x-autocad',
    'application/dwg',
    'application/x-dwg',
    'application/dxf',
    'application/x-dxf',
    'drawing/dwg',
    'image/vnd.dwg',
    'image/x-dwg',
  ];
  const DOCUMENT_EXTS = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.csv',
    '.ppt',
    '.pptx',
    '.dwg',
    '.dxf',
    '.dwt',
    '.dwf',
    '.dwfx',
    '.ifc',
    '.step',
    '.stp',
    '.iges',
    '.igs',
  ];
  const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
  const ATTACHMENT_ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.dwg,.dxf,.dwt,.dwf,.dwfx,.ifc,.step,.stp,.iges,.igs';
  const defaultTime = dayjs().hour(9).minute(0).second(0);
  const isCentralUser = useMemo(() => hasRole('CENTRAL_USER'), []);
  const currentUserId = useMemo(() => toNumericId(localStorage.getItem('id')), []);
  const shouldForceSelfAssignment = isCentralUser && currentUserId !== undefined;

  const extractExtension = (name: string) => {
    const clean = (name || '').split('?')[0].toLowerCase();
    const dotIndex = clean.lastIndexOf('.');
    return dotIndex >= 0 ? clean.slice(dotIndex) : '';
  };

  const resolveFileName = (file: Partial<UploadFile> | File) => {
    if ('name' in file && file.name) return file.name;
    if ('url' in file && typeof file.url === 'string') {
      const parts = file.url.split('/');
      return parts[parts.length - 1] || '';
    }
    return '';
  };

  const isImageFile = (file: Partial<UploadFile> | File) => {
    const type = 'type' in file ? file.type || '' : '';
    if (type.startsWith('image/')) return true;
    const ext = extractExtension(resolveFileName(file));
    return IMAGE_EXTS.includes(ext);
  };

  const isDocumentFile = (file: Partial<UploadFile> | File) => {
    const type = 'type' in file ? file.type || '' : '';
    if (DOCUMENT_MIME_TYPES.some((t) => type === t || type.startsWith(t))) return true;
    const ext = extractExtension(resolveFileName(file));
    return DOCUMENT_EXTS.includes(ext);
  };

  const getFileKind = (file: Partial<UploadFile> | File) => {
    if (isImageFile(file)) return 'image';
    if (isDocumentFile(file)) return 'document';
    return null;
  };

  const validateAttachmentFile = (file: Partial<UploadFile> | File) => {
    const kind = getFileKind(file);
    if (!kind) {
      openNotification('error', 'Hata', 'Sadece görsel veya doküman dosyaları yükleyebilirsiniz.');
      return null;
    }
    const size = 'size' in file ? file.size : undefined;
    if (size && size > MAX_UPLOAD_BYTES) {
      openNotification('error', 'Hata', 'Dosya boyutu 20MB sınırını aşıyor.');
      return null;
    }
    return kind;
  };

  const createUploadFile = (file: File): UploadFile => ({
    uid: `${Date.now()}-${file.name}-${Math.random().toString(16).slice(2)}`,
    name: file.name,
    size: file.size,
    type: file.type,
    originFileObj: file as any,
  });

  const splitAttachmentLists = (fileList: UploadFile[]) => {
    const images: UploadFile[] = [];
    const documents: UploadFile[] = [];
    fileList.forEach((file) => {
      const kind = getFileKind(file) || 'document';
      if (kind === 'image') {
        images.push(file);
      } else {
        documents.push(file);
      }
    });
    return { images, documents };
  };

  const normalizeTagValues = React.useCallback((rawTags: any): string[] => {
    if (!Array.isArray(rawTags)) return [];
    const seen = new Set<string>();
    const tags: string[] = [];
    rawTags.forEach((item: any) => {
      const rawName =
        typeof item === 'string'
          ? item
          : typeof item?.name === 'string'
            ? item.name
            : '';
      const normalized = rawName.trim().replace(/\s+/g, ' ');
      if (!normalized) return;
      const key = normalized.toLocaleLowerCase('tr-TR');
      if (seen.has(key)) return;
      seen.add(key);
      tags.push(normalized);
    });
    return tags;
  }, []);

  const fetchTagOptions = React.useCallback(async (search?: string) => {
    setLoadingTags(true);
    setTagError(null);
    try {
      const response: any = await ReminderService.listReminderTags(search?.trim() || undefined);
      const list = response?.data ?? response ?? [];
      const options = Array.isArray(list) ? list : [];
      setTagOptions(options);
    } catch (error) {
      console.error('Etiketler yüklenemedi', error);
      setTagError('Etiketler yüklenemedi.');
      setTagOptions([]);
    } finally {
      setLoadingTags(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      const tabKey = hideEntityTabs ? (initialValues?.entityTab || 'COMPANY') : initialValues?.entityTab || 'COMPANY';
      setEntityTab(tabKey);
      setProposalCompanyId((initialValues?.proposalCompanyId as number) ?? null);
      const defaultReminderDate = initialValues?.reminderDate
        ? dayjs(initialValues.reminderDate)
        : defaultTime;
      const defaultDueDate = initialValues?.dueDate ? dayjs(initialValues.dueDate) : undefined;
      form.resetFields();

      const parsedProperties = parseProperties(initialValues?.properties);
      const imagesList = buildUploadList(parsedProperties.images, 'image');
      const documentsList = buildUploadList(parsedProperties.documents, 'document');
      const resultImagesList = buildUploadList(parsedProperties.resultImages, 'result-image');
      const resultDocumentsList = buildUploadList(parsedProperties.resultDocuments, 'result-document');

      const resolvedState: ReminderStateType =
        (initialValues?.state as ReminderStateType) ??
        (typeof initialValues?.status === 'string' ? (initialValues?.status as ReminderStateType) : undefined) ??
        'PENDING';
      const resolvedStatusId =
        initialValues?.statusId ??
        (initialValues?.status && typeof initialValues.status === 'object' ? initialValues.status.id : undefined);

      form.setFieldsValue({
        ...initialValues,
        state: resolvedState,
        statusId: resolvedStatusId,
        reminderDate: defaultReminderDate,
        dueDate: defaultDueDate,
        estimatedDurationInput: formatEstimatedDurationMinutes(initialValues?.estimatedDurationMinutes),
        tags: normalizeTagValues((initialValues as any)?.tags),
        channel: normalizeChannelValue(initialValues?.channel),
        attachments: [...imagesList, ...documentsList],
        resultAttachments: [...resultImagesList, ...resultDocumentsList],
        properties: parsedProperties.raw,
        duplicateOnSave: false,
        duplicateContent: undefined,
        duplicateReminderDate: undefined,
      });
      if (shouldForceSelfAssignment) {
        form.setFieldsValue({ accountId: currentUserId });
      }
      setAttachments([...imagesList, ...documentsList]);
      setResultAttachments([...resultImagesList, ...resultDocumentsList]);
      setSelectedUserOverride(null);

      if (!users || users.length === 0) {
        UserService.getUsersWithoutPagination({ status: true })
          .then((resp: any) => {
            const list: any[] = resp?.data?.content ?? resp?.data ?? [];
            const filtered = list
              .filter((u) => hasAllowedReminderRole(u) && isUserActive(u));
            const sorted = sortUsersByName(filtered);
            setInternalUsers(sorted);
          })
          .catch((e: any) => {
            console.error('Görev Kullanıcıları yüklenemedi', e);
          });
      }

      fetchTagOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setUploadingAttachments(true);

      const uploadFileList = async (fileList?: UploadFile[], code: string = 'reminder-attachment') => {
        if (!fileList || !fileList.length) return [];
        const results: string[] = [];
        for (const file of fileList) {
          if (file.url) {
            results.push(file.url);
            continue;
          }
          if (file.originFileObj) {
            const fd = new FormData();
            fd.append('code', code);
            fd.append('document', file.originFileObj);
            const resp = await http.post('/documents', fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const data = resp?.data ?? resp;
            const url = data?.documentUrl || data?.filePath || data?.url || data;
            if (url) results.push(url);
          }
        }
        return results;
      };

      const { images: imageFiles, documents: documentFiles } = splitAttachmentLists(attachments);
      const { images: resultImageFiles, documents: resultDocumentFiles } = splitAttachmentLists(resultAttachments);
      const [imageUrls, documentUrls, resultImageUrls, resultDocumentUrls] = await Promise.all([
        uploadFileList(imageFiles, 'reminder-image'),
        uploadFileList(documentFiles, 'reminder-file'),
        uploadFileList(resultImageFiles, 'reminder-result-image'),
        uploadFileList(resultDocumentFiles, 'reminder-result-file'),
      ]);

      const basePropsRaw = (values as any).properties;
      let baseProps: any = {};
      if (basePropsRaw) {
        try {
          baseProps = typeof basePropsRaw === 'string' ? JSON.parse(basePropsRaw) : basePropsRaw;
        } catch {
          baseProps = {};
        }
      }
      const mergedProps = {
        ...(baseProps || {}),
        ...(imageUrls.length ? { images: imageUrls } : {}),
        ...(documentUrls.length ? { documents: documentUrls } : {}),
        ...(resultImageUrls.length ? { resultImages: resultImageUrls } : {}),
        ...(resultDocumentUrls.length ? { resultDocuments: resultDocumentUrls } : {}),
      };
      const properties =
        mergedProps && Object.keys(mergedProps).length ? JSON.stringify(mergedProps) : undefined;

      const parsedEstimatedDurationMinutes = isAdmin
        ? parseEstimatedDurationInput((values as any).estimatedDurationInput)
        : undefined;

      const {
        attachments: _attachments,
        resultAttachments: _resultAttachments,
        estimatedDurationInput: _estimatedDurationInput,
        ...rest
      } = values as any;
      const submitValues: ReminderFormValues = {
        ...rest,
        tags: normalizeTagValues((rest as any).tags),
        entityTab,
        ...(properties ? { properties } : {}),
        ...(isAdmin && parsedEstimatedDurationMinutes ? { estimatedDurationMinutes: parsedEstimatedDurationMinutes } : {}),
        ...(shouldForceSelfAssignment && currentUserId ? { accountId: currentUserId } : {}),
      };
      await onSubmit(submitValues);
      setUploadingAttachments(false);
    } catch (error: any) {
      setUploadingAttachments(false);
      if (error?.errorFields) {
        openNotification('error', 'Hata', 'Lütfen zorunlu alanları doldurunuz.');
        return;
      }
      throw error;
    }
  };

  const companiesWithFixed = useMemo(() => {
    if (fixedCompanyId && !companies.some((c: any) => Number(c.id) === Number(fixedCompanyId))) {
      return [...companies, { id: fixedCompanyId, name: fixedCompanyName || fixedCompanyId }];
    }
    return companies;
  }, [companies, fixedCompanyId, fixedCompanyName]);

  const proposalsWithFixed = useMemo(() => {
    if (fixedProposalId && !proposals.some((p: any) => Number(p.id) === Number(fixedProposalId))) {
      return [
        ...proposals,
        {
          id: fixedProposalId,
          proposalNumber: fixedProposalLabel || fixedProposalId,
          companyId: fixedCompanyId ?? proposalCompanyId ?? initialValues?.proposalCompanyId,
        },
      ];
    }
    return proposals;
  }, [proposals, fixedProposalId, fixedProposalLabel, fixedCompanyId, proposalCompanyId, initialValues]);

  const entityOptions = useMemo(
    () => (entityTab === 'PROPOSAL' ? proposalsWithFixed : companiesWithFixed),
    [entityTab, proposalsWithFixed, companiesWithFixed]
  );

  const selectedAccountId = useMemo(
    () =>
      shouldForceSelfAssignment
        ? currentUserId
        : toNumericId(selectedAccountIdValue ?? initialValues?.accountId),
    [selectedAccountIdValue, initialValues?.accountId, shouldForceSelfAssignment, currentUserId]
  );

  const normalizedUsers = useMemo(() => {
    if (users && users.length) return users;
    return internalUsers;
  }, [users, internalUsers]);

  const selectedUserFromSource = useMemo(() => {
    if (selectedAccountId === undefined) return undefined;
    return normalizedUsers.find((u) => getUserId(u) === selectedAccountId);
  }, [normalizedUsers, selectedAccountId]);

  useEffect(() => {
    if (!open || !isEdit || selectedAccountId === undefined) {
      setSelectedUserOverride(null);
      return;
    }
    if (selectedUserFromSource) {
      setSelectedUserOverride(null);
      return;
    }
    if (selectedUserOverride && getUserId(selectedUserOverride) === selectedAccountId) {
      return;
    }

    let cancelled = false;
    UserService.get(selectedAccountId)
      .then((resp: any) => {
        if (cancelled) return;
        const fetched = resp?.data ?? resp;
        if (fetched) {
          setSelectedUserOverride(fetched);
        }
      })
      .catch((error: any) => {
        if (cancelled) return;
        console.error('Pasif atanmis kullanici yuklenemedi', error);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isEdit, selectedAccountId, selectedUserFromSource, selectedUserOverride]);

  const effectiveUsers = useMemo(() => {
    const deduped = new Map<number, any>();

    normalizedUsers.forEach((user) => {
      const userId = getUserId(user);
      if (userId === undefined) return;
      if (!hasAllowedReminderRole(user)) return;
      if (!isUserActive(user)) return;
      deduped.set(userId, user);
    });

    if (selectedAccountId !== undefined) {
      const selectedUser = selectedUserFromSource ?? selectedUserOverride;
      if (selectedUser) {
        const selectedUserId = getUserId(selectedUser);
        if (selectedUserId !== undefined) {
          deduped.set(selectedUserId, selectedUser);
        }
      }
    }

    const sortedUsers = sortUsersByName(Array.from(deduped.values()));
    if (!shouldForceSelfAssignment || currentUserId === undefined) {
      return sortedUsers;
    }
    const selfUser =
      sortedUsers.find((u) => getUserId(u) === currentUserId) ||
      normalizedUsers.find((u) => getUserId(u) === currentUserId) ||
      selectedUserFromSource ||
      selectedUserOverride ||
      { id: currentUserId, firstName: 'Kullanıcı', lastName: `#${currentUserId}` };
    return [selfUser];
  }, [
    normalizedUsers,
    selectedAccountId,
    selectedUserFromSource,
    selectedUserOverride,
    shouldForceSelfAssignment,
    currentUserId,
  ]);

  const effectiveMaxDate = useMemo(() => {
    if (maxReminderDate) return maxReminderDate;
    if (entityTab !== 'PROPOSAL' || !proposalIdValue) return undefined;
    const proposal = proposals.find((p) => String(p.id) === String(proposalIdValue));
    if (!proposal) return undefined;
    const statusId = proposal.statusId ?? proposal.status?.id;
    const statusName = proposal.status?.name || proposal.statusName || proposal.statusTitle;
    const normalizedStatus = normalizeTurkish(String(statusName || '')).toLowerCase();
    if (statusId === 3 || normalizedStatus.includes('teklif gonderildi') || normalizedStatus.includes('teklif gönderildi')) {
      return dayjs().add(30, 'day').endOf('day');
    }
    if (statusId === 12 || normalizedStatus.includes('manuel takip')) {
        return dayjs().add(60, 'day').endOf('day');
    }
    return undefined;
  }, [maxReminderDate, entityTab, proposalIdValue, proposals]);

  const disabledDate = (date: dayjs.Dayjs) => {
    if (!date) return false;
    const today = dayjs().startOf('day');
    const shouldEnforceMax = effectiveMaxDate ? effectiveMaxDate.isSame(today, 'day') || effectiveMaxDate.isAfter(today, 'day') : false;
    const isAfterMax = shouldEnforceMax ? date.isAfter(effectiveMaxDate as dayjs.Dayjs, 'day') : false;
    const isSunday = date.day() === 0;
    return isAfterMax || isSunday;
  };

  useEffect(() => {
    if (entityTab !== 'PROPOSAL' || !proposalIdValue) return;
    const matchedProposal = proposalsWithFixed.find((p: any) => String(p.id) === String(proposalIdValue));
    const statusId =
      matchedProposal?.statusId ??
      (matchedProposal?.status && typeof matchedProposal.status === 'object' ? matchedProposal.status.id : undefined);
    if (!statusId) return;
    const currentStatusId = form.getFieldValue('statusId');
    if (currentStatusId !== statusId) {
      form.setFieldsValue({ statusId });
    }
  }, [entityTab, proposalIdValue, proposalsWithFixed, form]);

  const disableCompanySelect = !!fixedCompanyId || hideEntityTabs || isEdit;

  const formatUserOption = (user: any) => {
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    const roleTitle = (user?.roles ?? [])
      .map((r: any) => r?.title || r?.name)
      .filter(Boolean)
      .join(', ');
    const base = fullName || user.email || getUserId(user);
    return roleTitle ? `${base} (${roleTitle})` : base;
  };

  const selectFilter = (input: string, option?: any) => {
    const label = option?.label ?? option?.children;
    return normalizeTurkish(String(label || '')).includes(normalizeTurkish(input || ''));
  };

  const renderChannelOptionLabel = (option: (typeof REMINDER_CHANNEL_OPTIONS)[number]) => {
    const Icon = option.icon;
    return (
      <Space size={8}>
        <Tooltip title={option.label}>
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Icon size={16} />
          </span>
        </Tooltip>
        <span>{option.label}</span>
      </Space>
    );
  };

  function parseProperties(raw: any): { images?: string[]; documents?: string[]; resultImages?: string[]; resultDocuments?: string[]; raw?: any } {
    if (!raw) return {};
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return {
        images: Array.isArray(parsed?.images) ? parsed.images : undefined,
        documents: Array.isArray(parsed?.documents) ? parsed.documents : undefined,
        resultImages: Array.isArray(parsed?.resultImages) ? parsed.resultImages : undefined,
        resultDocuments: Array.isArray(parsed?.resultDocuments) ? parsed.resultDocuments : undefined,
        raw: parsed,
      };
    } catch {
      return {};
    }
  }

  const buildUploadList = (urls?: string[], prefix: string = 'file') => {
    if (!Array.isArray(urls)) return [];
    return urls
      .filter((u) => typeof u === 'string' && u.length)
      .map((url, idx) => ({
        uid: `${prefix}-${idx}-${url}`,
        name: url.split('/').pop() || `file-${idx + 1}`,
        url,
        status: 'done' as const,
        type: prefix.includes('image') ? 'image/*' : 'application/octet-stream',
      }));
  };

  const handlePasteAttachments = (
    event: React.ClipboardEvent<HTMLDivElement>,
    setList: React.Dispatch<React.SetStateAction<UploadFile[]>>
  ) => {
    if (uploadingAttachments) return;
    const files = Array.from(event.clipboardData?.files ?? []);
    if (!files.length) return;
    const next: UploadFile[] = [];
    files.forEach((file) => {
      const kind = validateAttachmentFile(file);
      if (!kind) return;
      next.push(createUploadFile(file));
    });
    if (!next.length) return;
    event.preventDefault();
    setList((prev) => [...prev, ...next]);
  };

  const renderUnifiedAttachmentField = (
    label: string,
    helperText: string,
    fileList: UploadFile[],
    setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>,
    listType: 'picture' | 'text' = 'text'
  ) => (
    <Form.Item label={label}>
      <div onPaste={(event) => handlePasteAttachments(event, setFileList)}>
        <Upload.Dragger
          multiple
          accept={ATTACHMENT_ACCEPT}
          listType={listType}
          fileList={fileList}
          beforeUpload={(file) => {
            const kind = validateAttachmentFile(file);
            if (!kind) return Upload.LIST_IGNORE;
            return false;
          }}
          onChange={({ fileList: nextList }) => setFileList(nextList)}
          disabled={uploadingAttachments}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <CursorInWindowIcon size={40} />
            <div style={{ fontSize: 12, color: '#1B1E27' }}>Sürükleyip bırakın, seçin veya Ctrl+V ile yapıştırın.</div>
            <div style={{ fontSize: 12, color: '#8C8C8C' }}>{helperText}</div>
          </div>
        </Upload.Dragger>
      </div>
    </Form.Item>
  );


  const stateOptions: ReminderStateType[] = useMemo(() => {
    if (isAdmin) return REMINDER_STATE_TYPES;
    const initialState = (initialValues?.state as ReminderStateType | undefined) ?? undefined;
    const base: ReminderStateType[] = ['COMPLETED'];
    if (initialState && !base.includes(initialState)) base.unshift(initialState);
    if (stateValue && !base.includes(stateValue)) base.unshift(stateValue);
    return base;
  }, [isAdmin, stateValue, initialValues]);

  return (
    <Drawer
      title={isEdit ? 'Görev Güncelle' : 'Yeni Görev Ekle'}
      open={open}
      onClose={onClose}
      width={768}
      destroyOnClose
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>İptal</Button>
          <Button type="primary" onClick={handleSubmit}>
            Kaydet
          </Button>
        </Space>
      }
    >
      <Form layout="vertical" form={form} initialValues={initialValues}>
        {isAdmin && (
          <Form.Item
            label="Kullanıcı"
            name="accountId"
            rules={[{ required: true, message: 'Kullanıcı seçiniz' }]}
          >
            <Select
              showSearch
              allowClear={!shouldForceSelfAssignment}
              placeholder="Kullanıcı seçiniz"
              optionFilterProp="label"
              filterOption={selectFilter}
              disabled={shouldForceSelfAssignment}
            >
              {effectiveUsers.map((u) => {
                const optionUserId = getUserId(u);
                if (optionUserId === undefined) return null;
                const label = formatUserOption(u);
                return (
                  <Select.Option key={optionUserId} value={optionUserId} label={label}>
                    {label}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="statusId" hidden>
          <Input type="hidden" />
        </Form.Item>

        {!hideEntityTabs && (
          <Tabs
            activeKey={entityTab}
            onChange={(key) => {
              const tabKey = key as 'COMPANY' | 'PROPOSAL';
              setEntityTab(tabKey);
              const currentCompanyId = form.getFieldValue('proposalCompanyId');
              setProposalCompanyId(currentCompanyId || null);
            }}
            items={[
              { key: 'COMPANY', label: 'Firma' },
              { key: 'PROPOSAL', label: 'Teklif' },
            ]}
          />
        )}

        {entityTab === 'COMPANY' && (
          <Form.Item
            label="Firma"
            name="companyId"
          >
            <Select
              showSearch
              allowClear
              placeholder="Firma seçiniz"
              optionFilterProp="label"
              filterOption={selectFilter}
              disabled={disableCompanySelect}
            >
              {entityOptions.map((item: any) => (
                <Select.Option key={item.id} value={item.id} label={item.name || item.title || item.id}>
                  {item.name || item.title || item.id}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {entityTab === 'PROPOSAL' && (
          <>
            <Form.Item
              label="Firma"
              name="proposalCompanyId"
            >
              <Select
                showSearch
                allowClear
                placeholder="Firma seçiniz"
                optionFilterProp="label"
                filterOption={selectFilter}
                onChange={(val) => {
                  setProposalCompanyId(val || null);
                  form.setFieldsValue({ proposalId: undefined });
                }}
                disabled={hideEntityTabs || isEdit}
              >
                {companiesWithFixed.map((item: any) => (
                  <Select.Option key={item.id} value={item.id} label={item.name || item.title || item.id}>
                    {item.name || item.title || item.id}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Teklif"
              name="proposalId"
            >
              <Select
                showSearch
                allowClear
                placeholder="Teklif seçiniz"
                optionFilterProp="label"
                filterOption={selectFilter}
                disabled={!!fixedProposalId || hideEntityTabs || isEdit || !proposalCompanyId}
              >
                {proposalsWithFixed
                  .filter((p: any) => {
                    if (!proposalCompanyId) return false;
                    const cid = p.companyId ?? p.company?.id;
                    return String(cid) === String(proposalCompanyId);
                  })
                  .map((item: any) => {
                    const code = item.proposalNumber || item.proposalCode || item.id;
                    const dateText = item.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY') : '';
                    const label = dateText ? `${code} - ${dateText}` : `${code}`;
                    return (
                      <Select.Option
                        key={item.id}
                        value={item.id}
                        label={label}
                      >
                        {label}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </>
        )}

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label="Görev Tarihi"
              name="reminderDate"
              rules={[{ required: true, message: 'Görev Tarihi seçiniz' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY HH:mm"
                showTime={{ format: 'HH:mm', minuteStep: 5, defaultValue: defaultTime }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Bitiş Tarihi" name="dueDate">
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY HH:mm"
                showTime={{ format: 'HH:mm', minuteStep: 5, defaultValue: defaultTime }}
              />
            </Form.Item>
          </Col>
        </Row>

        {isAdmin && (
          <Form.Item
            label={
              <Space size={6}>
                <span>Tahmini Süre</span>
                <Tooltip title={estimatedDurationTooltipContent}>
                  <span style={{ color: '#8C8C8C', cursor: 'help', borderBottom: '1px dashed #BFBFBF' }}>
                    a/h/g/s/d
                  </span>
                </Tooltip>
              </Space>
            }
            name="estimatedDurationInput"
            rules={[
              {
                validator: async (_, value) => {
                  if (!value || !String(value).trim()) {
                    return Promise.resolve();
                  }
                  try {
                    parseEstimatedDurationInput(String(value));
                    return Promise.resolve();
                  } catch (error: any) {
                    return Promise.reject(new Error(error?.message || ESTIMATED_DURATION_VALIDATION_MESSAGE));
                  }
                },
              },
            ]}
            extra="Örnek: 1a 1h 1g 10s 15d"
          >
            <Input placeholder="Örn. 1a 1h 1g 10s 15d" />
          </Form.Item>
        )}

        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="İş Adımı"
              name="activityType"
              rules={[{ required: true, message: 'İş Adımı seçiniz.' }]}
            >
              <Space wrap size={8}>
                {ACTIVITY_TYPES.map((type) => (
                  <Tag.CheckableTag
                    key={type}
                    checked={activityTypeValue === type}
                    onChange={(checked) => checked && form.setFieldsValue({ activityType: type })}
                    style={{
                      cursor: 'pointer',
                      borderColor: activityTypeValue === type ? ACTIVITY_COLORS[type] || '#1677ff' : undefined,
                      background: activityTypeValue === type ? '#f0f5ff' : undefined,
                      height: 36,
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                    }}
                  >
                    <span style={{ color: '#444', fontWeight: activityTypeValue === type ? 600 : 500 }}>
                      {ACTIVITY_LABELS[type]}
                    </span>
                  </Tag.CheckableTag>
                ))}
              </Space>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="İletişim Yöntemi" name="channel">
          <Select
            showSearch
            allowClear
            placeholder="İletişim yöntemi seçiniz"
            optionFilterProp="label"
            filterOption={selectFilter}
          >
            {REMINDER_CHANNEL_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value} label={option.label}>
                {renderChannelOptionLabel(option)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="İçerik"
          name="content"
          rules={[{ required: true, message: 'İçerik giriniz' }]}
        >
          <Input.TextArea rows={3} placeholder="Görev içeriği" />
        </Form.Item>

        <Form.Item label="Etiketler" name="tags">
          <TaggableMultiSelect
            options={tagOptions}
            loading={loadingTags}
            error={tagError}
            placeholder="Etiket seçiniz veya yazınız"
            onSearch={fetchTagOptions}
          />
        </Form.Item>

        {isEdit && (isAdmin || canComplete) && (
          <Form.Item label="Durum" name="state">
            <Select placeholder="Durum seçiniz" allowClear>
              {stateOptions.map((s: ReminderStateType) => (
                <Select.Option key={s} value={s}>
                  {STATE_LABELS[s] || s}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {renderUnifiedAttachmentField(
          'Ekler',
          'Görsel, doküman ve CAD dosyalarını tek alandan yükleyin. Maks 20MB',
          attachments,
          setAttachments,
          'picture'
        )}

        {showMeetingFields && (
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item label={linkLabel} name="link">
                <Input placeholder="Bilgi giriniz" />
              </Form.Item>
            </Col>
          </Row>
        )}

      </Form>
    </Drawer>
  );
};

export default ReminderDrawer;
